/* eslint-disable jsx-a11y/anchor-has-content */
import { useState } from "react";
import QuotePageController from "./QuotePage.controller";
import {
  ButtonDS,
  CTABanner,
  DownloadableFiles,
  GlobalIcon,
  Price,
  TextWithInfoIcon,
} from "get-life-storybook-ts";
import { DOMAIN, PHONE_NOT_COVER } from "../../../constants/Global";
import { LanguageT } from "../../../context/GetLifeContext.context";
import {
  QuotePageBenefitsList,
  GlobalShowTruspilot,
} from "../../../utils/InternationlChanges";

import "./QuotePage.styles.scss";
import Spinner from "get-life-storybook-ts/lib/components/Icons/Spinner";
import QuoteBanner from "./components/QuoteBanner";
import PendingCapitalAdjustmentModal from "../../../components/PendingCapitalAdjustmentModal/PendingCapitalAdjustmentModal";
import NativeQuotePage from "./NativeQuotePage";
import { isNativeMode } from "../../../native/nativeMode";

const CAPITAL_STEP = 5000;

const formatCapital = (value: number): string =>
  new Intl.NumberFormat(
    process.env.REACT_APP_DOMAIN_LOCALE === "fr" ? "fr-FR" : "es-ES"
  ).format(value);

const sliderBackground = (value: number, min: number, max: number): string => {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  // Color del mercado (App.js: IT #003399, AXA WL #1943A0), como el resto del slider.
  const fill = "var(--primary-color, #423EF4)";
  const track = "var(--light-primary-color, #ECF0FF)";
  return `linear-gradient(to right, ${fill} 0%, ${fill} ${pct}%, ${track} ${pct}%, ${track} 100%)`;
};

const listPoints = (translate: (key: string) => string): string[] => {
  const points =
    QuotePageBenefitsList[DOMAIN!] || QuotePageBenefitsList["default"];
  return points.map((p) => translate(p));
};

const LegacyQuotePage = () => {
  const {
    price,
    afterPromoText,
    beforePromoText,
    btnContractLabel,
    conditionsPrice,
    downloadableFiles,
    downloadableLoading,
    downloadDocument,
    handleButtonQuote,
    hasTelesubscription,
    isMobile,
    linkRefCall,
    loadingQuote,
    navigateBack,
    setFlagLink,
    translate,
    isStickyVisible,
    guaranteeOptions,
    handleCapitalChange,
    traitState,
    pendingAdjustmentModal,
  } = QuotePageController();

  const deathGuarantee = guaranteeOptions.find((g) => g.key === "DEATH");
  const deathMin = deathGuarantee?.minCapital ?? 0;
  const deathMax = deathGuarantee?.maxCapital ?? 0;
  const deathCapital = deathGuarantee?.capital ?? 0;
  const [pendingDeath, setPendingDeath] = useState<number | null>(null);
  const sliderValue = pendingDeath ?? deathCapital;

  const commitDeath = () => {
    if (pendingDeath !== null && pendingDeath !== deathCapital) {
      handleCapitalChange("DEATH", pendingDeath);
    }
  };

  const coveredSeriousIllnesses = [
    translate("calculatorPage.cancer"),
    translate("calculatorPage.infarct"),
    translate("calculatorPage.stroke"),
    translate("calculatorPage.renalInsufficiency"),
    translate("calculatorPage.paralysis"),
    translate("calculatorPage.vitalOrganTransplant"),
    translate("calculatorPage.alzheimer"),
    translate("calculatorPage.parkinson"),
  ];

  return (
    <main className="QuotePage">
      <section className="QuotePage__journeyquestion" id="quote_page_banner">
        <QuoteBanner
          showTruspilot={GlobalShowTruspilot[DOMAIN as LanguageT]}
          textUpperButton={translate("calculatorPage.textUpperButton")}
          containerStyle="heroBannerStyle w-full"
          subtitleTextStyle="subtitleTextStyle"
          title={""}
          subtitle={price !== 0 ? translate("calculatorPage.textYourPrice") : ""}
          btnLabel={DOMAIN === "fr" ? undefined : btnContractLabel}
          btnOnClick={handleButtonQuote}
          callmeLabel={
            hasTelesubscription
              ? translate("calculatorPage.QuotePage.maxCapital.button").replace(
                "$",
                `${PHONE_NOT_COVER}`
              )
              : ""
          }
          callmeLabelOnClick={() => setFlagLink(true)}
          content={price !== 0 ? (
            <div className="flex md:justify-center">
              <Price
                after={afterPromoText}
                decimals={conditionsPrice("decimal")}
                frequency={
                  beforePromoText
                    ? beforePromoText
                    : translate("calculatorPage.QuotePage.from")
                }
                price={conditionsPrice()}
                priceLabel={translate("calculatorPage.textMonthly")}
                loading={loadingQuote}
              />
            </div>
          ) : (
            <>
              <div className="flex md:justify-center">
                <div className="relative w-fit">
                  <div className={`flex flex-col gap-[4px] w-fit transition-all md:items-center md:gap-[16px]`} style={{ opacity: loadingQuote ? 0 : 1 }}>
                    <span className={`font-normal text-[60px] leading-[60px] md:text-[80px] md:leading-[80px] tracking-[-0.02em]`}>{translate("calculator.price.notAvailable")}</span>
                  </div>
                  <span
                    className="absolute top-0 left-0 flex items-center justify-center w-full h-full pointer-events-none transition-all"
                    style={{ opacity: loadingQuote ? 1 : 0 }}
                  >
                    <Spinner />
                  </span>
                </div>
              </div>
            </>
          )
          }
          trustpilot={translate("calculatorPage.QuotePage.trustpilot")}
          actionLabel={translate("calculatorPage.QuotePage.actionLabel")}
          subContent={undefined}
        />
        <a href={`tel:${PHONE_NOT_COVER}`} ref={linkRefCall} />
      </section>
      <section className="QuotePage__cancelanytime">
        {deathGuarantee && deathMax > deathMin && (
          <div className="QuotePage__deathSlider md:py-[80px] md:px-[160px] px-[25px] py-[50px]">
            <div className="QuotePage__deathSlider-label">
              {translate("upsell.guarantees.capital")}
            </div>
            <div className="QuotePage__deathSlider-trackWrapper">
              <div
                className="QuotePage__deathSlider-bubble"
                style={{
                  left: `${
                    ((sliderValue - deathMin) / (deathMax - deathMin)) * 100
                  }%`,
                }}
              >
                {formatCapital(sliderValue)} €
              </div>
              <input
                type="range"
                className="QuotePage__deathSlider-input"
                min={deathMin}
                max={deathMax}
                step={CAPITAL_STEP}
                value={sliderValue}
                onChange={(e) => setPendingDeath(parseInt(e.target.value, 10))}
                onPointerUp={commitDeath}
                onKeyUp={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    commitDeath();
                  }
                }}
                disabled={deathGuarantee.loading}
                aria-label={`${translate("upsell.guarantees.capital")} ${deathGuarantee.label}`}
                style={{
                  background: sliderBackground(sliderValue, deathMin, deathMax),
                }}
              />
            </div>
            <div className="QuotePage__deathSlider-range">
              <span>{formatCapital(deathMin)} €</span>
              <span>{formatCapital(deathMax)} €</span>
            </div>
            {(DOMAIN === "es" || DOMAIN === "wl_es") && (
              <p className="QuotePage__deathSlider-hint">
                <GlobalIcon iconName="InfoIcon" size="XS" color="#8e8e8e" />
                <span>{translate("calculatorPage.QuotePage.coverageHint")}</span>
              </p>
            )}
          </div>
        )}
        {traitState.isTraitActive("SERIOUS_ILLNESS") ? (
          <div className="QuotePage__seriousIllnesses">
            <h3 className="H3">
              {translate("calculatorPage.includedSeriousIllnesses")}
            </h3>
            <div className="QuotePage__seriousIllnessesList">
              {coveredSeriousIllnesses.map((illness, idx) => {
                return (
                  <div
                    key={`${illness}_${idx}`}
                    className="QuotePage__seriousIllnessesElement"
                  >
                    <GlobalIcon iconName="CircleCheckFilledIcon" size="XXXS" />
                    <span className="BodyM font-MW">{illness}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        {downloadableFiles && (
          <div className="QuotePage__cancelanytime--downloadFiles">
            <TextWithInfoIcon
              content={translate("calculatorPage.downloadableFilesTitle")}
              infoIcon={true}
              weight="400"
              variant="BodyXSmall"
              color="secondarygrey"
              iconSize="XXXS"
              iconColor="#7B7B7B"
            />

            <DownloadableFiles
              files={downloadableFiles}
              className="md:!flex-row"
              loading={downloadableLoading}
              onClick={downloadDocument}
            />
          </div>
        )}
      </section>
      <section className="QuotePage__journeyquestion--cta">
        <CTABanner
          title={translate("calculatorPage.textQuestions")}
          subtitle={translate("calculatorPage.textHelp")}
          subtitleStyle="text-darkGray60"
          listPoints={listPoints(translate)}
          listStyle="text-darkGray80"
        />
      </section>

      <section className="QuotePage__buttons">
        {DOMAIN !== "fr" && <ButtonDS
          label={btnContractLabel}
          onClick={handleButtonQuote}
          rightIcon="ArrowRightIcon"
          className="QuotePage__buttons--primary"
        />}
        <ButtonDS
          ghost
          buttonType="secondary"
          label={translate("back")}
          onClick={() => navigateBack()}
          leftIcon="ArrowBackIcon"
          className="QuotePage__buttons--secondary"
        />
      </section>
      <div
        className={`${isMobile ? "QuotePage__buttons--sticky" : "hidden"}`}
        style={{
          opacity: isStickyVisible ? 1 : 0,
        }}
      >
        {hasTelesubscription && (
          <ButtonDS
            buttonType="secondary"
            ghost
            label={translate(
              "calculatorPage.QuotePage.maxCapital.button.sticky"
            )}
            onClick={() => setFlagLink(true)}
          />
        )}
        {DOMAIN !== "fr" && <ButtonDS
          className="!w-full"
          label={btnContractLabel}
          onClick={handleButtonQuote}
        />}
      </div>
      <PendingCapitalAdjustmentModal {...pendingAdjustmentModal} />
    </main>
  );
};

// Native (Portal Cliente / funnel organic IT): misma lógica, diseño del marco
// native. Resto de mercados: la página de siempre.
const QuotePage = () => (isNativeMode() ? <NativeQuotePage /> : <LegacyQuotePage />);

export default QuotePage;
